import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  addPlaylist as addPlaylistService,
  deletePlaylist as deletePlaylistService,
  addTrackToPlaylist as addTrackToPlaylistService,
  removeTrackFromPlaylist as removeTrackFromPlaylistService,
  updatePlaylistName as updatePlaylistNameService,
  clearPlaylistTracks as clearPlaylistTracksService
} from '../services/playlist.service';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, onSnapshot, query, orderBy, where } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { useOrganizationStore } from '../store/organization.store';

const PlaylistContext = createContext();

export const usePlaylists = () => useContext(PlaylistContext);

export const PlaylistProvider = ({ children }) => {
  const [playlists, setPlaylists] = useState([]);
  const [focusedPlaylists, setFocusedPlaylists] = useState([]); // Array of playlist IDs
  const [selectedPlaylist, setSelectedPlaylist] = useState(null); // For filtering
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playlistTrackIds, setPlaylistTrackIds] = useState({}); // { [playlistId]: [trackId, ...] }
  const [tracks] = useState({});
  const [user, setUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);

  const auth = getAuth();
  const db = getFirestore(getApp());

  // Get current organization from the store
  const currentOrgId = useOrganizationStore(state => state.currentOrgId);

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Force refresh the ID token to ensure Firestore has valid auth
        await firebaseUser.getIdToken(true);
      }
      setUser(firebaseUser);
      setUserLoading(false);
    });
    return () => unsubscribe();
  }, [auth]);

  // Fetch playlists in real-time for the current user in the current organization
  useEffect(() => {
    if (!user || !currentOrgId) {
      setPlaylists([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const userId = user.uid;

    // Query playlists in the organization filtered by userId
    const playlistsRef = collection(db, 'organizations', currentOrgId, 'playlists');
    const q = query(
      playlistsRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'asc')
    );

    const unsub = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPlaylists(data);
      setLoading(false);
    }, (err) => {
      console.error('Error fetching playlists:', err);
      setError(err);
      setLoading(false);
    });

    return () => unsub();
  }, [user, db, currentOrgId]);

  // Clear playlists state when organization changes
  useEffect(() => {
    // Reset playlist-related state when org changes
    setFocusedPlaylists([]);
    setSelectedPlaylist(null);
    setPlaylistTrackIds({});
  }, [currentOrgId]);

  // Real-time listeners for playlist track IDs
  useEffect(() => {
    if (!user || !currentOrgId || playlists.length === 0) {
      setPlaylistTrackIds({});
      return;
    }

    const unsubscribers = {};
    let isMounted = true;

    playlists.forEach(playlist => {
      const tracksRef = collection(db, 'organizations', currentOrgId, 'playlists', playlist.id, 'tracks');
      unsubscribers[playlist.id] = onSnapshot(tracksRef, snapshot => {
        if (!isMounted) return;
        setPlaylistTrackIds(prev => ({
          ...prev,
          [playlist.id]: snapshot.docs.map(doc => doc.id)
        }));
      });
    });

    // Clean up listeners on unmount or playlists change
    return () => {
      isMounted = false;
      Object.values(unsubscribers).forEach(unsub => unsub && unsub());
    };
  }, [user, playlists, db, currentOrgId]);

  // Playlist CRUD actions
  const addPlaylist = useCallback(async (name, description = '') => {
    if (!currentOrgId) {
      setError(new Error('No organization selected'));
      return;
    }

    // Optimistically add a temporary playlist
    const tempId = `temp-${Date.now()}`;
    const tempPlaylist = { id: tempId, name, description };
    setPlaylists(prev => [...prev, tempPlaylist]);
    setLoading(true);
    try {
      await addPlaylistService(name, description);
    } catch (err) {
      // Revert UI if error
      setPlaylists(prev => prev.filter(p => p.id !== tempId));
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [currentOrgId]);

  const deletePlaylist = useCallback(async (playlistId) => {
    // Optimistically remove playlist
    const prevPlaylists = playlists;
    setPlaylists(prev => prev.filter(p => p.id !== playlistId));
    setLoading(true);
    try {
      await deletePlaylistService(playlistId);
      setFocusedPlaylists(focused => focused.filter(id => id !== playlistId));
      if (selectedPlaylist === playlistId) setSelectedPlaylist(null);
    } catch (err) {
      // Revert UI if error
      setPlaylists(prevPlaylists);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [playlists, selectedPlaylist]);

  const addTrackToPlaylist = useCallback(async (playlistId, trackId) => {
    // Optimistically update UI
    setPlaylistTrackIds(prev => ({
      ...prev,
      [playlistId]: [...(prev[playlistId] || []), trackId]
    }));
    try {
      await addTrackToPlaylistService(playlistId, trackId);
    } catch (err) {
      // Revert UI if error
      setPlaylistTrackIds(prev => ({
        ...prev,
        [playlistId]: prev[playlistId].filter(id => id !== trackId)
      }));
      setError(err);
    }
  }, []);

  const removeTrackFromPlaylist = useCallback(async (playlistId, trackId) => {
    // Optimistically update UI
    setPlaylistTrackIds(prev => ({
      ...prev,
      [playlistId]: (prev[playlistId] || []).filter(id => id !== trackId)
    }));
    try {
      await removeTrackFromPlaylistService(playlistId, trackId);
    } catch (err) {
      // Revert UI if error
      setPlaylistTrackIds(prev => ({
        ...prev,
        [playlistId]: [...(prev[playlistId] || []), trackId]
      }));
      setError(err);
    }
  }, []);

  // Add updatePlaylistName action
  const updatePlaylistName = useCallback(async (playlistId, name) => {
    // Optimistically update playlist name
    const prevPlaylists = playlists;
    setPlaylists(prev => prev.map(p => p.id === playlistId ? { ...p, name } : p));
    setLoading(true);
    try {
      await updatePlaylistNameService(playlistId, name);
    } catch (err) {
      // Revert UI if error
      setPlaylists(prevPlaylists);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [playlists]);

  // Add clearPlaylistTracks action
  const clearPlaylistTracks = useCallback(async (playlistId) => {
    // Optimistically clear tracks
    const prevTracks = playlistTrackIds[playlistId] || [];
    setPlaylistTrackIds(prev => ({ ...prev, [playlistId]: [] }));
    setLoading(true);
    try {
      await clearPlaylistTracksService(playlistId);
    } catch (err) {
      // Revert UI if error
      setPlaylistTrackIds(prev => ({ ...prev, [playlistId]: prevTracks }));
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [playlistTrackIds]);

  // Focused playlists logic
  const toggleFocusedPlaylist = (playlistId) => {
    setFocusedPlaylists(focused =>
      focused.includes(playlistId)
        ? focused.filter(id => id !== playlistId)
        : [...focused, playlistId]
    );
  };

  // Select playlist for filtering
  const selectPlaylist = (playlistId) => {
    setSelectedPlaylist(playlistId);
  };

  // Clear all focused playlists
  const clearFocusedPlaylists = () => setFocusedPlaylists([]);

  return (
    <PlaylistContext.Provider
      value={{
        playlists,
        loading,
        error,
        addPlaylist,
        deletePlaylist,
        addTrackToPlaylist,
        removeTrackFromPlaylist,
        focusedPlaylists,
        toggleFocusedPlaylist,
        clearFocusedPlaylists,
        selectedPlaylist,
        selectPlaylist,
        setError,
        playlistTrackIds,
        updatePlaylistName,
        clearPlaylistTracks,
        tracks,
        currentOrgId, // Expose for components that need to check
        userLoading,
      }}
    >
      {children}
    </PlaylistContext.Provider>
  );
};
